import { ValidationErrors } from '@angular/forms';
import type { ReadonlyFieldState, ValidationError } from '@angular/forms/signals';
import { ValidationErrorValues } from './validation.util';

/**
 * Structural view over the built-in Signal Forms errors (`NgValidationError`).
 *
 * The public `ValidationError` interface only exposes `kind` and `message`; the constraint that was
 * violated lives on the concrete error subclass. Reading it through this shape keeps the mapping
 * below free of per-subclass type guards.
 */
interface SignalFormsErrorDetails extends ValidationError {
  readonly min?: number;
  readonly max?: number;
  readonly minDate?: Date;
  readonly maxDate?: Date;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly pattern?: RegExp;
}

/**
 * Length of a Signal Forms field value, measured the way the framework's own length validators do:
 * `length` when present (string, array), otherwise `size` (`Set`, `Map`).
 */
const valueLength = (value: unknown): number => {
  const sized = value as { length?: number; size?: number } | null | undefined;
  if (typeof sized?.length === 'number') return sized.length;
  return sized?.size ?? 0;
};

/** Maps a single Signal Forms error onto the reactive-forms error keys understood by `ValidationMessage`. */
const mapError = (error: ValidationError, value: unknown): ValidationErrorValues => {
  const { kind, message } = error;

  // A validator that supplied its own message short-circuits the structured mapping: `ValidationMessage`
  // renders `errorMessage` verbatim (after translation), which is exactly what the message is for.
  if (message) return { errorMessage: message };

  const details: SignalFormsErrorDetails = error;

  switch (kind) {
    case 'required':
      return { required: true };
    case 'minLength':
      return { minlength: { requiredLength: details.minLength ?? 0, actualLength: valueLength(value) } };
    case 'maxLength':
      return { maxlength: { requiredLength: details.maxLength ?? 0, actualLength: valueLength(value) } };
    case 'min':
      return { min: { min: String(details.min), actual: String(value) } };
    case 'max':
      return { max: { max: String(details.max), actual: String(value) } };
    case 'minDate':
      return { minDate: { minDate: details.minDate as Date, actual: value as Date } };
    case 'maxDate':
      return { maxDate: { maxDate: details.maxDate as Date, actual: value as Date } };
    case 'pattern':
      return { pattern: { pattern: String(details.pattern), actual: String(value) } };
    case 'email':
      return { email: kind };
    default:
      // Custom `validate()` kinds without a message: surface the kind so it is at least visible.
      return { [kind]: true };
  }
};

/**
 * Adapts the errors of a Signal Forms field to the reactive-forms `ValidationErrors` shape used across
 * the app (`ValidationMessage`, `KzInputDirective[errorMessage]`, …).
 *
 * Use it to bind Signal Forms fields to the existing shared inputs without duplicating error copy:
 * ```ts
 * readonly loginErrors = computed(() => fieldValidationErrors(this.logInForm.login()));
 * ```
 *
 * @param state Field state, i.e. the result of calling a field (`form.login()`).
 * @returns Reactive-forms style errors, or `null` when the field is valid.
 */
export const fieldValidationErrors = (state: ReadonlyFieldState<unknown>): ValidationErrors | null => {
  const errors = state.errors();
  if (errors.length === 0) return null;

  const value = state.value();
  return errors.reduce<ValidationErrorValues>(
    (accumulator, error) => ({ ...accumulator, ...mapError(error, value) }),
    {}
  );
};
